import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Grid,
  Typography,
  Container,
  CardMedia,
  Tooltip as MuiToolTip,
  Box,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { PieChart } from '@mui/x-charts/PieChart';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { styled } from '@mui/material/styles';
import { useAppDispatch } from 'store/hooks';
import {
  getWellbeingScore,
  getWellbeingTrackerDetails,
} from 'store/slices/mcatSlice';
import { getAppointmentConfirmationDetails } from 'store/slices/appointmentSlice';
import dayjs from 'dayjs';

interface TrackerData {
  date: string;
  score: number;
}

const StyledCategory = styled('text')({
  fill: '#344054',
  textAnchor: 'middle',
  dominantBaseline: 'central',
  fontSize: '16px',
  fontWeight: 600,
});

const StyledScore = styled('text')({
  textAnchor: 'middle',
  dominantBaseline: 'central',
  fontSize: 24,
});

const getWellnessScoreColor = (category: string) => {
  switch (category) {
    case 'In Crisis':
      return '#F04438';
    case 'Critical':
      return '#FF4405';
    case 'Struggling':
      return '#EF6820';
    case 'Vulnerable':
      return '#F79009';
    case 'Balanced':
      return '#EAAA08';
    case 'Stable':
      return '#66C61C';
    default:
      return '#12B76A';
  }
};

const categorizeScore = (score: number): string => {
  if (score <= 100) return 'In Crisis';
  if (score <= 200) return 'Critical';
  if (score <= 450) return 'Struggling';
  if (score <= 650) return 'Vulnerable';
  if (score <= 800) return 'Balanced';
  if (score <= 900) return 'Stable';
  return 'Thriving';
};

const getAccordionDetailUI = (score: number) => {
  if (score <= 100)
    return (
      <ul>
        <li>Seek urgent psychiatric help immediately, including emergency services.</li>
        <li>Ensure a safe environment and minimize potential risks for harm.</li>
        <li>Provide supportive presence and maintain open communication.</li>
      </ul>
    );
  if (score <= 200)
    return (
      <ul>
        <li>Schedule an urgent appointment with a mental health professional.</li>
        <li>Monitor safety and prevent isolation.</li>
        <li>Encourage self-care and relaxation techniques.</li>
      </ul>
    );
  if (score <= 450)
    return (
      <ul>
        <li>Encourage regular psychiatry visits and therapy sessions.</li>
        <li>Check emotional well-being and relationship struggles.</li>
        <li>Promote coping tools like journaling or meditation.</li>
      </ul>
    );
  if (score <= 650)
    return (
      <ul>
        <li>Continue regular therapy sessions to manage challenges.</li>
        <li>Maintain regular emotional check-ins.</li>
        <li>Encourage creative coping strategies.</li>
      </ul>
    );
  if (score <= 800)
    return (
      <ul>
        <li>Encourage ongoing therapy to maintain balance and stability.</li>
        <li>Reinforce coping mechanisms for occasional stressors.</li>
        <li>Ensure regular emotional check-ins.</li>
      </ul>
    );
  if (score <= 900)
    return (
      <ul>
        <li>Celebrate emotional achievements and stability.</li>
        <li>Foster positive social interactions.</li>
        <li>Encourage sharing the wellness journey.</li>
      </ul>
    );
  return (
    <ul>
      <li>Praise resilience and relationship building.</li>
      <li>Motivate setting higher wellness goals.</li>
      <li>Promote community engagement for fulfillment.</li>
    </ul>
  );
};

const getAccordionText = (score: number) => {
  if (score <= 100)
    return 'Severe distress disrupting daily functioning. Immediate psychiatric help is required.';
  if (score <= 200)
    return 'Approaching severe distress that impairs functioning. Prompt intervention is necessary.';
  if (score <= 450)
    return 'Regular emotional challenges impacting daily life. Support and therapy recommended.';
  if (score <= 650)
    return 'Often overwhelmed by stress, affecting relationships. Counseling is beneficial.';
  if (score <= 800)
    return 'Healthy mental state with occasional disturbances. Maintain balance through therapy.';
  if (score <= 900)
    return 'Consistently good mental health with resilience and positive relationships.';
  return 'Flourishing mental state with strong purpose, resilience, and well-being.';
};

function PieCenterLabel({ children }: any) {
  return (
    <>
      <StyledCategory x={175} y={160}>
        {children[1]}
      </StyledCategory>
      <StyledScore
        x={175}
        y={190}
        fontWeight={600}
        fill={getWellnessScoreColor(children[1])}
      >
        {children[0]}
      </StyledScore>
    </>
  );
}

const WellnessChart = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const numericPatientId = Number(patientId);
  const [score, setScore] = useState<number>(0);
  const [category, setCategory] = useState<string>('NA');
  const [trackerPeriod, setTrackerPeriod] = useState('6M');
  const [data, setData] = useState<TrackerData[]>([]);
  const [nextCheckIn, setNextCheckIn] = useState<string | null>(null);
  const [showOverAll, setShowOverAll] = useState<boolean>(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!numericPatientId) return;

    const fetchData = async () => {
      try {
        const [scoreRes, apptRes] = await Promise.all([
          dispatch(getWellbeingScore(numericPatientId)).unwrap(),
          dispatch(getAppointmentConfirmationDetails(numericPatientId)).unwrap(),
        ]);

        if (scoreRes?.status === 200) {
          const wellbeingScore = scoreRes?.data?.wellnessScore;
          setScore(wellbeingScore);
          setCategory(categorizeScore(wellbeingScore));
        }

        if (apptRes?.status === 200) {
          const date = new Date(apptRes.data);
          if (date > new Date()) {
            setNextCheckIn(dayjs(date).format('MM/DD/YYYY hh:mm A'));
          }
        }
      } catch (err) {
        console.error('Error fetching wellness data:', err);
      }
    };

    fetchData();
  }, [dispatch, numericPatientId]);

  useEffect(() => {
    if (!numericPatientId) return;

    const body = { period: trackerPeriod, patientId: numericPatientId };
    dispatch(getWellbeingTrackerDetails(body))
      .unwrap()
      .then((res: any) => {
        if (res.status === 200) {
          const years = res.data.map((d: any) => d.date.slice(-4));
          const uniqueYears = Array.from(new Set(years));
          setShowOverAll(uniqueYears.length > 1);
          setData(res.data);
        }
      })
      .catch((err) => console.error('Error fetching tracker data:', err));
  }, [dispatch, trackerPeriod, numericPatientId]);

  const handleTrackerPeriod = (period: string) => setTrackerPeriod(period);

  return (
    <Container component="main" maxWidth="md" sx={{ py: 4 }}>
      <Grid>
        <Grid item sx={{ textAlign: 'center' }}>
          <Box pt="16px" display="flex" alignItems="center" flexDirection="column">
            <CardMedia
              component="img"
              src="../images/wellnessdashboard.svg"
              alt="dashboard"
              sx={{ width: 70, height: 'auto' }}
            />
            <Typography
              variant="h5"
              sx={{
                position: 'relative',
                textTransform: 'capitalize',
                color: '#1C1F24',
              }}
            >
              Wellness Dashboard
              <CardMedia
                component="img"
                src="../images/vector.svg"
                alt="divider"
                sx={{
                  position: 'absolute',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: { xs: 70, sm: 120, md: 160 },
                  height: 'auto',
                }}
              />
            </Typography>
            <Typography
              variant="body1"
              sx={{ mt: 2, fontSize: '18px', color: '#1C1F24' }}
            >
              Wellness Wayfinder: Empower Your Child&apos;s Journey with Clarity.
            </Typography>
          </Box>
        </Grid>

        {/* Pie chart */}
        <Grid item>
          <Box
            mt="30px"
            p="20px 14px"
            sx={{
              background: '#fff',
              borderRadius: '12px',
              border: '1px solid #EFF4FF',
              boxShadow: '0px 4px 16px rgba(0,0,0,0.12)',
            }}
          >
            <Typography variant="h2" color="#1C1F24">
              Mindweal Wellness Score
            </Typography>

            <Grid container justifyContent="center" mt={2}>
              <PieChart
                series={[
                  {
                    data: [
                      { id: 0, value: 50, label: 'i-Wise', color: '#FFFCF5' },
                      { id: 1, value: 20, label: 'Patient Survey', color: '#FFFCF5' },
                      { id: 2, value: 30, label: 'Guardian Survey', color: '#FFFCF5' },
                    ],
                    innerRadius: 70,
                    outerRadius: 170,
                    paddingAngle: 5,
                    cornerRadius: 15,
                    startAngle: -90,
                    endAngle: 360,
                    cx: 170,
                    cy: 170,
                  },
                ]}
                tooltip={{ trigger: 'none' }}
                legend={{ hidden: true }}
                sx={{
                  '.MuiPieArc-root': { strokeWidth: '1.5px' },
                  '.MuiPieArcLabel-root': {
                    fontSize: '14px',
                    fontWeight: 600,
                    fill: '#475467',
                  },
                  '.MuiPieArc-series-auto-generated-id-0': {
                    stroke: '#FFC44D',
                  },
                }}
                width={350}
                height={400}
              >
                <PieCenterLabel>
                  {score}
                  {category}
                </PieCenterLabel>
              </PieChart>
            </Grid>

            {/* Accordion */}
            <Grid mt={5}>
              <Accordion
                sx={{
                  boxShadow: 'none',
                  border: '1px solid #ccc',
                  mb: 2,
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon color="primary" />}
                  sx={{ background: '#F9FAFB' }}
                >
                  <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#1C1F24' }}>
                    What does my wellness score{' '}
                    <span
                      style={{
                        color: getWellnessScoreColor(category),
                        paddingLeft: '4px',
                      }}
                    >
                      {score}
                    </span>{' '}
                    mean?
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography color="#1C1F24" mt={2} mb={3} fontWeight={600}>
                    {category}:
                  </Typography>
                  <Typography color="#1C1F24" mb={3}>
                    {getAccordionText(score)}
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion
                sx={{
                  boxShadow: 'none',
                  border: '1px solid #ccc',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon color="primary" />}
                  sx={{ background: '#F9FAFB' }}
                >
                  <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#1C1F24' }}>
                    What I can do
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography color="#1C1F24" mt={2} mb={3} fontWeight={600}>
                    {category}:
                  </Typography>
                  <Typography color="#1C1F24" mb={3}>
                    {getAccordionDetailUI(score)}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default WellnessChart;
